"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Play, Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GlobalTestimonials() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: logosRef,
    isVisible: logosVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const testimonials = [{
    quote: "The VR immersion during the Berlin techno set was hauntingly real. I felt the bass in my soul and the sweat of the crowd from my living room.",
    name: "Kaelen V.",
    meta: "Member - VR Access - Berlin Ritual",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_kaelen.png",
    stars: 5,
    featured: false
  }, {
    quote: "Finally, a platform that treats nightlife like the high art it is. The 8K stream quality is unmatched.",
    name: "Soren M.",
    meta: "Guest - Live Stream - Tokyo Underground",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_soren.png",
    stars: 5,
    featured: false
  }, {
    quote: "Step inside the Church. Watch how we transformed a 14th-century cathedral into a temple of bass and light for our winter solstice gathering.",
    name: "The High Priestess",
    meta: "Featured Archive - Solstice Gathering",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_priestess.png",
    stars: 5,
    featured: true,
    thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/ritual_video_thumbnail_solstice.png"
  }, {
    quote: "The individual event purchase was seamless. I bought the art show stream and felt like I was walking through the gallery in real-time.",
    name: "Elena R.",
    meta: "Guest - Digital Gallery - Neo-Noir Exhibit",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_elena.png",
    stars: 5,
    featured: false
  }, {
    quote: "Unlimited access to the vault is the best investment I've made. Every concert, every secret set, all available in stunning detail.",
    name: "Marcus T.",
    meta: "Vault Member - London Residency",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_marcus.png",
    stars: 5,
    featured: false
  }, {
    quote: "The spatial audio in the VR experience is what sets this apart. You don't just watch the event; you are the event.",
    name: "Liora K.",
    meta: "VR Initiate - Ibiza Sunset Set",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_liora.webp",
    stars: 5,
    featured: false
  }, {
    quote: "A deliberate, trustworthy platform for the most indulgent experiences. The Church never misses a beat.",
    name: "Dante S.",
    meta: "Member - Milan Fashion Afterparty",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697006/img/initiate_avatar_dante.webp",
    stars: 5,
    featured: false
  }];
  const publications = [{
    name: "Resident Advisor"
  }, {
    name: "Mixmag"
  }, {
    name: "Hypebeast"
  }, {
    name: "Wired"
  }, {
    name: "Rolling Stone"
  }, {
    name: "Vogue"
  }];
  return <section data-section-id="705" id="global-testimonials" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4 sm:px-6 lg:px-8">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: "radial-gradient(var(--primary) 1px, transparent 1px)",
      backgroundSize: "4px 4px"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`max-w-3xl transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase font-mono text-primary">
            The Testimonies
          </span>
          <h2 className="mt-6 font-default font-extrabold tracking-tighter text-foreground text-4xl sm:text-5xl lg:text-[3.5rem] leading-[0.9] uppercase">
            Witness the Over Indulgence.
          </h2>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="size-5 fill-primary text-primary" />)}
            </div>
            <p className="text-sm font-mono text-muted-foreground">
              <span className="text-foreground font-bold">5.0</span> / 
              Digital Sanctum Verified
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {testimonials.map((t, i) => <Card key={i} data-index={i} className={`group relative bg-card/40 border border-border backdrop-blur-md p-8 transition-all duration-300 hover:-translate-y-1 hover:ring-1 hover:ring-primary/50 hover:shadow-xl rounded-md ${t.featured ? "lg:col-span-2" : ""}`}>
              {}
              {t.featured && t.thumbnail && <div className="relative mb-6 overflow-hidden rounded-md border border-border">
                  <img src={t.thumbnail} data-wvc-srcset={`${t.thumbnail}-wvc-srcset`} data-wvc-sizes={`${t.thumbnail}-wvc-sizes`} alt={`${t.name} ritual footage`} loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[20%] contrast-125" />
                  <div className="absolute inset-0 bg-secondary/40 mix-blend-multiply" />
                  <Button size="icon" className="absolute inset-0 m-auto size-16 rounded-md bg-primary text-primary-foreground hover:bg-destructive hover:text-destructive-foreground shadow-lg transition-all duration-100" aria-label="Enter the ritual">
                    <Play className="size-6 fill-current" />
                  </Button>
                </div>}

              {}
              <div className="flex items-center gap-1">
                {Array.from({
              length: t.stars
            }).map((_, idx) => <Star key={idx} data-index={idx} className="size-4 fill-primary text-primary" />)}
              </div>

              {}
              <blockquote className={`mt-5 font-default text-foreground leading-relaxed tracking-tight ${t.featured ? "text-xl sm:text-2xl font-bold" : "text-lg font-medium"}`}>
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {}
              <div className="mt-8 flex items-center gap-3">
                <SafeHydrate><Avatar className="size-10 border border-border rounded-md">
                  <AvatarImage src={t.avatar} alt={t.name} />
                  <AvatarFallback className="bg-muted text-muted-foreground text-xs font-mono rounded-md">
                    {t.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="min-w-0">
                  <p className="font-mono font-bold text-primary text-sm truncate uppercase tracking-wider">
                    {t.name}
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground truncate uppercase">
                    {t.meta}
                  </p>
                </div>
              </div>
            </Card>)}
        </div>

        {}
        <div ref={logosRef} className={`mt-20 transition-all duration-700 ease-out delay-300 ${logosVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase font-mono text-muted-foreground text-center">
            Broadcast Partners
          </p>
          <div className="mt-6 overflow-x-auto">
            <div className="flex items-center justify-start sm:justify-center gap-10 sm:gap-14 min-w-max px-4">
              {publications.map((pub, i) => <span key={i} data-index={i} className="font-mono text-lg sm:text-xl text-muted-foreground/50 whitespace-nowrap tracking-tighter transition-colors hover:text-primary">
                  {pub.name}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}